/* eslint-disable max-len */
import type { Options as BaseTileOptions } from "ol/layer/BaseTile";
import type { Options as LayerGroupOptions } from "ol/layer/Group";
import LayerGroup from "ol/layer/Group";
import TileLayer from "ol/layer/Tile";
import XYZSource from "ol/source/XYZ";

/**
 * 地理院タイルの標準地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiStd = (options?: { visible: boolean }) => {
    return new TileLayer({
        title: "標準地図",
        type: "base",
        visible: options?.visible ?? true,
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/std/{z}/{x}/{y}.png",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "The bathymetric contours are derived from those contained within the GEBCO Digital Atlas, published by the BODC on behalf of IOC and IHO (2003) (https://www.gebco.net)",
                // "海上保安庁許可第292502号（水路業務法第25条に基づく類似刊行物）",
                // "Shoreline data is derived from: United States. National Imagery and Mapping Agency.",
                // "\"Vector Map Level 0 (VMAP0).\" Bethesda, MD: Denver, CO: The Agency; USGS Information Services, 1997.",
            ],
        }),
    } as BaseTileOptions<XYZSource>);
};

/**
 * 地理院タイルの標準地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiStdEnglish = (options?: { visible: boolean }) => {
    return new TileLayer({
        title: "English",
        type: "base",
        visible: options?.visible ?? true,
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/english/{z}/{x}/{y}.png",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "The bathymetric contours are derived from those contained within the GEBCO Digital Atlas, published by the BODC on behalf of IOC and IHO (2003) (https://www.gebco.net)",
                // "海上保安庁許可第292502号（水路業務法第25条に基づく類似刊行物）",
                // "Shoreline data is derived from: United States. National Imagery and Mapping Agency.",
                // "\"Vector Map Level 0 (VMAP0).\" Bethesda, MD: Denver, CO: The Agency; USGS Information Services, 1997.",
            ],
        }),
    } as BaseTileOptions<XYZSource>);
};

/**
 * 地理院タイルの淡色地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiPale = (options?: { visible: boolean }) => {
    return new TileLayer({
        title: "淡色地図",
        type: "base",
        visible: options?.visible ?? true,
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "Shoreline data is derived from: United States. National Imagery and Mapping Agency.",
                // "\"Vector Map Level 0 (VMAP0).\" Bethesda, MD: Denver, CO: The Agency; USGS Information Services, 1997.",
            ],
        }),
    } as BaseTileOptions<XYZSource>);
};

/**
 * 地理院タイルの白地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiBlank = (options?: { visible: boolean }) => {
    return new TileLayer({
        title: "白地図",
        type: "base",
        visible: options?.visible ?? true,
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/blank/{z}/{x}/{y}.png",
            minZoom: 5,
            maxZoom: 14,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
            ],
        }),
    } as BaseTileOptions<XYZSource>);
};


/**
 * 地理院タイルの地図空中写真レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiSeamlessPhoto = (options?: { visible: boolean }) => {
    return new TileLayer({
        title: "空中写真",
        type: "base",
        visible: options?.visible ?? true,
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "Images on 世界衛星モザイク画像 obtained from site https://lpdaac.usgs.gov/data_access maintained by the NASA Land Processes Distributed Active Archive Center (LP DAAC), USGS/Earth Resources Observation and Science (EROS) Center, Sioux Falls, South Dakota, (Year). Source of image data product.",
                // "データソース：Landsat8画像（GSI,TSIC,GEO Grid/AIST）, Landsat8画像（courtesy of the U.S. Geological Survey）, 海底地形（GEBCO）",
                // "GRUS画像（© Axelspace）",
            ],
        }),
    } as BaseTileOptions<XYZSource>);
};

/**
 * 地理院タイルの地図空中写真+白地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiPhotoAndBlank = (options?: { visible: boolean }) => {
    const photo = new TileLayer({
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "Images on 世界衛星モザイク画像 obtained from site https://lpdaac.usgs.gov/data_access maintained by the NASA Land Processes Distributed Active Archive Center (LP DAAC), USGS/Earth Resources Observation and Science (EROS) Center, Sioux Falls, South Dakota, (Year). Source of image data product.",
                // "データソース：Landsat8画像（GSI,TSIC,GEO Grid/AIST）, Landsat8画像（courtesy of the U.S. Geological Survey）, 海底地形（GEBCO）",
                // "GRUS画像（© Axelspace）",
            ],
        }),
    });
    const blank = new TileLayer({
        className: "multiply-layer",
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/blank/{z}/{x}/{y}.png",
            minZoom: 5,
            maxZoom: 14,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
            ],
        }),
    });

    return new LayerGroup({
        title: "空中写真",
        type: "base",
        combine: true,
        visible: options?.visible ?? true,
        layers: [photo, blank],
    } as LayerGroupOptions);
};

/**
 * 地理院タイルの地図空中写真+白地図レイヤーを作成する。
 *
 * @param options 表示オプション
 * @param options.visible レイヤーを表示するかどうか
 * @returns レイヤー
 */
export const gsiPhotoAndPale = (options?: { visible: boolean }) => {
    const photo = new TileLayer({
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "Images on 世界衛星モザイク画像 obtained from site https://lpdaac.usgs.gov/data_access maintained by the NASA Land Processes Distributed Active Archive Center (LP DAAC), USGS/Earth Resources Observation and Science (EROS) Center, Sioux Falls, South Dakota, (Year). Source of image data product.",
                // "データソース：Landsat8画像（GSI,TSIC,GEO Grid/AIST）, Landsat8画像（courtesy of the U.S. Geological Survey）, 海底地形（GEBCO）",
                // "GRUS画像（© Axelspace）",
            ],
        }),
    });
    const pale = new TileLayer({
        className: "multiply-layer",
        source: new XYZSource({
            url: "https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png",
            minZoom: 2,
            maxZoom: 18,
            attributions: [
                "<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>",
                // "Shoreline data is derived from: United States. National Imagery and Mapping Agency.",
                // "\"Vector Map Level 0 (VMAP0).\" Bethesda, MD: Denver, CO: The Agency; USGS Information Services, 1997.",
            ],
        }),
    });

    return new LayerGroup({
        title: "空中写真",
        type: "base",
        combine: true,
        visible: options?.visible ?? true,
        layers: [photo, pale],
    } as LayerGroupOptions);
};
